/**
 * Student Profile Dashboard — Academic Curator
 * Tabbed carousel with inline-editable contacts table.
 * "Modify" enters edit mode; "Add" inserts a blank row; "Save" persists via GraphQL.
 */

import { useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAppAuth, useIsAdminOrStaff } from '@/lib/mock-auth';
import { useEntityName } from '@/lib/EntityContext';
import { type Contact, createContact, updateContact, deleteContact, reorderContacts } from '@/lib/data';
import { useProfileData } from '@/lib/hooks/useProfileData';
import { validateContactForm } from '@/lib/contact-validation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import {
  Pencil, Calendar, Shield, X, Save, Plus, Trash2, GripVertical,
  Mail, Phone, MapPin, Globe, Star,
  AlertCircle, CheckCircle2, FileText, BarChart3, Wallet,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ContactEditDialog } from '@/components/ContactEditDialog';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/* ── Constants ── */

const TABS = [
  { id: 'contacts', label: 'Contacts', icon: Mail, enabled: true },
  { id: 'records', label: 'Records', icon: FileText, enabled: false },
  { id: 'results', label: 'Results', icon: BarChart3, enabled: false },
  { id: 'finance', label: 'Finance', icon: Wallet, enabled: false },
] as const;

type TabId = (typeof TABS)[number]['id'];

const CONTACT_TYPE_NAMES: Record<number, string> = {
  1: 'Email', 2: 'Cellphone', 3: 'Phone', 4: 'Pager', 5: 'Fax',
  6: 'Street Address', 7: 'Skype', 8: 'ICQ', 9: 'MSN', 10: 'AIM',
  11: 'Website', 12: 'Emergency Contact', 13: 'Work Address', 14: 'Work Email',
  15: 'Work Phone', 16: 'Work Phone 2', 17: 'Permanent Home Address',
  18: 'Postal Address', 19: 'Homestay Code',
};

const ADDRESS_TYPE_IDS = new Set([6, 13, 17, 18]);

const TYPE_ICONS: Record<number, React.ReactNode> = {
  1: <Mail className="w-4 h-4" />, 2: <Phone className="w-4 h-4" />,
  3: <Phone className="w-4 h-4" />, 4: <Phone className="w-4 h-4" />,
  5: <Phone className="w-4 h-4" />, 6: <MapPin className="w-4 h-4" />,
  7: <Globe className="w-4 h-4" />, 8: <Globe className="w-4 h-4" />,
  9: <Globe className="w-4 h-4" />, 10: <Globe className="w-4 h-4" />,
  11: <Globe className="w-4 h-4" />, 12: <Star className="w-4 h-4" />,
  13: <MapPin className="w-4 h-4" />, 14: <Mail className="w-4 h-4" />,
  15: <Phone className="w-4 h-4" />, 16: <Phone className="w-4 h-4" />,
  17: <MapPin className="w-4 h-4" />, 18: <MapPin className="w-4 h-4" />,
  19: <MapPin className="w-4 h-4" />,
};

const TYPE_COLORS: Record<number, string> = {
  1: 'bg-accent text-primary', 2: 'bg-blue-50 text-blue-600',
  3: 'bg-blue-50 text-blue-600', 4: 'bg-blue-50 text-blue-600',
  5: 'bg-slate-50 text-slate-600', 6: 'bg-emerald-50 text-emerald-600',
  7: 'bg-sky-50 text-sky-600', 8: 'bg-sky-50 text-sky-600',
  9: 'bg-sky-50 text-sky-600', 10: 'bg-sky-50 text-sky-600',
  11: 'bg-sky-50 text-sky-600', 12: 'bg-red-50 text-red-600',
  13: 'bg-emerald-50 text-emerald-600', 14: 'bg-purple-50 text-purple-600',
  15: 'bg-purple-50 text-purple-600', 16: 'bg-purple-50 text-purple-600',
  17: 'bg-emerald-50 text-emerald-600', 18: 'bg-amber-50 text-amber-600',
  19: 'bg-amber-50 text-amber-600',
};

type EditableContact = Contact & { _isNew?: boolean; _deleted?: boolean };

function getPlaceholder(typeId: number): string {
  if ([1, 14].includes(typeId)) return 'user@example.com';
  if ([2, 3, 4, 5, 15, 16].includes(typeId)) return '+61412345678';
  if (ADDRESS_TYPE_IDS.has(typeId)) return '123 Main St, City, State 12345';
  if ([7, 8, 9, 10].includes(typeId)) return 'username';
  if (typeId === 11) return 'https://example.com';
  if (typeId === 12) return 'Contact name & number';
  if (typeId === 19) return 'Homestay code';
  return 'Enter value';
}

/** Validate a single contact row using the shared validation module */
function validateRow(contact: EditableContact): string[] {
  const cv = contact.contactValue;
  return validateContactForm({
    typeId: contact.typeId,
    val: cv.val || '',
    building: cv.building || '',
    unit: cv.unit || '',
    streetNumber: cv.streetNumber || '',
    streetName: cv.streetName || '',
    locality: cv.locality || '',
    region: cv.region || '',
    postcode: cv.postcode || '',
    country: cv.country || '',
  });
}

/* ── Save contacts via GraphQL ── */
async function saveContactsToApi(studentId: string, contacts: EditableContact[], reorderedTypes: Set<number>): Promise<void> {
  const toCreate = contacts.filter((c) => c._isNew && !c._deleted);
  const toDelete = contacts.filter((c) => !c._isNew && c._deleted);
  const toUpdate = contacts.filter((c) => !c._isNew && !c._deleted);

  const ADDRESS_TYPES = new Set([6, 13, 17, 18]);

  // Run all mutations sequentially — rank is embedded in DynamoDB keys,
  // so parallel mutations on the same type cause transaction conflicts.
  const tempToRealId = new Map<string, string>();
  for (const c of toCreate) {
    const isAddr = ADDRESS_TYPES.has(c.typeId);
    const created = await createContact({
      userId: studentId,
      typeId: c.typeId,
      val: isAddr ? undefined : c.contactValue.val,
      building: isAddr ? (c.contactValue.building || undefined) : undefined,
      unit: isAddr ? (c.contactValue.unit || undefined) : undefined,
      streetNumber: isAddr ? (c.contactValue.streetNumber || undefined) : undefined,
      streetName: isAddr ? (c.contactValue.streetName || undefined) : undefined,
      locality: isAddr ? (c.contactValue.locality || undefined) : undefined,
      region: isAddr ? (c.contactValue.region || undefined) : undefined,
      postcode: isAddr ? (c.contactValue.postcode || undefined) : undefined,
      country: isAddr ? (c.contactValue.country || undefined) : undefined,
      isActive: c.isActive,
      isRecovery: c.isRecovery,
    });
    tempToRealId.set(c.id, created.id);
  }
  for (const c of toUpdate) {
    const isAddr = ADDRESS_TYPES.has(c.typeId);
    await updateContact(c.id, {
      val: isAddr ? undefined : c.contactValue.val,
      building: isAddr ? (c.contactValue.building || undefined) : undefined,
      unit: isAddr ? (c.contactValue.unit || undefined) : undefined,
      streetNumber: isAddr ? (c.contactValue.streetNumber || undefined) : undefined,
      streetName: isAddr ? (c.contactValue.streetName || undefined) : undefined,
      locality: isAddr ? (c.contactValue.locality || undefined) : undefined,
      region: isAddr ? (c.contactValue.region || undefined) : undefined,
      postcode: isAddr ? (c.contactValue.postcode || undefined) : undefined,
      country: isAddr ? (c.contactValue.country || undefined) : undefined,
      isActive: c.isActive,
      isRecovery: c.isRecovery,
    });
  }
  for (const c of toDelete) {
    await deleteContact(c.id);
  }

  // Apply rank changes via dedicated reorder mutation (per type group).
  // Only send for types where the user reordered via drag-and-drop.
  if (reorderedTypes.size > 0) {
    const surviving = contacts.filter((c) => !c._deleted && reorderedTypes.has(c.typeId));
    const typeGroups = new Map<number, EditableContact[]>();
    for (const c of surviving) {
      const list = typeGroups.get(c.typeId);
      if (list) list.push(c);
      else typeGroups.set(c.typeId, [c]);
    }
    for (const group of typeGroups.values()) {
      const sorted = group.sort((a, b) => a.rank - b.rank);
      const ids = sorted.map((c) => c._isNew ? tempToRealId.get(c.id)! : c.id).filter(Boolean);
      if (ids.length > 1) {
        await reorderContacts(ids);
      }
    }
  }
}

/* ── Contact Detail Sidebar ── */
function ContactSidebar({ contact, onClose }: { contact: Contact; onClose: () => void }) {
  const typeName = CONTACT_TYPE_NAMES[contact.typeId] || `Type ${contact.typeId}`;
  const value = contact.contactValue.val || contact.contactValue.formattedAddress || '';
  const icon = TYPE_ICONS[contact.typeId] || <Globe className="w-5 h-5" />;
  const colorClass = TYPE_COLORS[contact.typeId] || 'bg-surface-low text-muted-foreground';

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-[1px] z-40" onClick={onClose} aria-hidden="true" />
      <div className="fixed inset-y-0 right-0 w-full sm:max-w-sm bg-white shadow-2xl z-50 flex flex-col animate-slide-in-right" role="dialog" aria-modal="true" aria-label={`${typeName} contact detail`}>
        <div className="p-6 pb-4 flex items-center justify-between border-b border-surface-low">
          <h3 className="text-lg font-extrabold text-foreground tracking-tight">Contact Detail</h3>
          <button onClick={onClose} className="p-2 hover:bg-surface-low rounded-full transition-colors" aria-label="Close contact detail">
            <X className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${colorClass} flex items-center justify-center`}>{icon}</div>
            <div>
              <p className="text-sm font-bold text-foreground">{typeName}</p>
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Rank {contact.rank}</p>
            </div>
          </div>
          <div className="bg-surface-low p-4 rounded-2xl">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">Value</p>
            <p className="text-sm font-semibold text-foreground break-all">{value}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-surface-low p-4 rounded-2xl">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-2">Status</p>
              {contact.isActive ? <Badge variant="success">Active</Badge> : contact.bounced ? <Badge variant="destructive">Suspended</Badge> : <Badge variant="secondary">Inactive</Badge>}
            </div>
            <div className="bg-surface-low p-4 rounded-2xl">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-2">Bounced</p>
              <div className="flex items-center gap-2">
                {contact.bounced
                  ? <><AlertCircle className="w-4 h-4 text-destructive" aria-hidden="true" /><span className="text-sm font-semibold text-destructive">{contact.bounceCount}×</span></>
                  : <><CheckCircle2 className="w-4 h-4 text-emerald-500" aria-hidden="true" /><span className="text-sm font-semibold text-emerald-600">Clean</span></>}
              </div>
            </div>
          </div>
          <div className="bg-surface-low p-4 rounded-2xl">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-2">Recovery</p>
            {contact.isRecovery
              ? <span className="text-sm text-primary font-semibold">Yes — Recovery Contact</span>
              : <span className="text-sm text-muted-foreground italic">Not a recovery contact</span>}
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Editable Contact Row (sortable) ── */
function EditableRow({
  contact,
  onChange,
  onDelete,
  onOpenDialog,
}: {
  contact: EditableContact;
  onChange: (updated: EditableContact) => void;
  onDelete: () => void;
  onOpenDialog?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: contact.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  const isAddress = ADDRESS_TYPE_IDS.has(contact.typeId);
  const isEmergency = contact.typeId === 12;
  const needsDialog = isAddress || isEmergency;
  const value = contact.contactValue.val || contact.contactValue.formattedAddress || '';
  const errors = validateRow(contact);
  const hasError = errors.length > 0;

  const updateValue = (v: string) => {
    const cv = contact.contactValue;
    if (isAddress) {
      onChange({ ...contact, contactValue: { ...cv, formattedAddress: v, val: undefined } });
    } else {
      onChange({ ...contact, contactValue: { ...cv, val: v, formattedAddress: undefined } });
    }
  };

  // Build a summary string for address/emergency types
  const summary = isAddress
    ? [contact.contactValue.streetNumber, contact.contactValue.streetName, contact.contactValue.locality, contact.contactValue.region, contact.contactValue.postcode, contact.contactValue.country].filter(Boolean).join(', ') || 'Click edit to add address'
    : isEmergency
      ? value || 'Click edit to add contact'
      : value;

  return (
    <TableRow ref={setNodeRef} style={style} className={cn('transition-colors', contact._deleted && 'opacity-40 line-through')}>
      {/* Drag handle + Rank */}
      <TableCell className="py-2 sm:py-3 w-16">
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground touch-none"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="w-4 h-4" aria-hidden="true" />
          </button>
          <span className="text-xs font-bold text-muted-foreground w-5 text-center">{contact.rank}</span>
        </div>
      </TableCell>

      {/* Type */}
      <TableCell className="py-2 sm:py-3">
        {needsDialog ? (
          <span className="text-xs sm:text-sm font-medium text-foreground px-1.5">
            {CONTACT_TYPE_NAMES[contact.typeId] || `Type ${contact.typeId}`}
          </span>
        ) : (
          <select
            value={contact.typeId}
            onChange={(e) => onChange({ ...contact, typeId: Number(e.target.value) })}
            className="bg-surface-low border-none rounded-lg text-xs sm:text-sm py-1 sm:py-1.5 pl-1.5 sm:pl-2 pr-6 sm:pr-8 focus:ring-2 focus:ring-primary/20 cursor-pointer w-full"
            disabled={contact._deleted}
            aria-label="Contact type"
          >
            {Object.entries(CONTACT_TYPE_NAMES)
              .filter(([id]) => !ADDRESS_TYPE_IDS.has(Number(id)) && Number(id) !== 12)
              .map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
          </select>
        )}
      </TableCell>

      {/* Value */}
      <TableCell className="py-2 sm:py-3">
        {needsDialog ? (
          <div className="flex items-center gap-2">
            <p className={cn('text-sm truncate flex-1', !value && 'text-muted-foreground italic')}>
              {summary}
            </p>
            <button
              type="button"
              onClick={onOpenDialog}
              disabled={contact._deleted}
              className="shrink-0 p-1.5 hover:bg-surface-low rounded-lg text-muted-foreground hover:text-primary transition-all disabled:opacity-40"
              aria-label="Edit in dialog"
            >
              <Pencil className="w-4 h-4" />
            </button>
            {hasError && (
              <p className="text-[10px] text-destructive font-medium shrink-0">{errors[0]}</p>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            <Input
              value={value}
              onChange={(e) => updateValue(e.target.value)}
              placeholder={getPlaceholder(contact.typeId)}
              disabled={contact._deleted}
              className={cn(hasError && value ? 'border-destructive focus-visible:ring-destructive/20' : '')}
            />
            {hasError && value && (
              <p className="text-[10px] text-destructive font-medium">{errors[0]}</p>
            )}
          </div>
        )}
      </TableCell>

      {/* Active toggle */}
      <TableCell className="py-2 sm:py-3">
        <button
          type="button"
          onClick={() => onChange({ ...contact, isActive: !contact.isActive })}
          disabled={contact._deleted}
          className={cn(
            'w-10 h-5 rounded-full relative p-0.5 flex items-center transition-all',
            contact.isActive ? 'bg-primary justify-end' : 'bg-surface-high justify-start'
          )}
          role="switch"
          aria-checked={contact.isActive}
          aria-label={`Active status for contact rank ${contact.rank}`}
        >
          <div className="w-4 h-4 bg-white rounded-full shadow-sm" />
        </button>
      </TableCell>

      {/* Delete */}
      <TableCell className="py-2 sm:py-3 text-center">
        <button
          onClick={onDelete}
          className="p-1.5 hover:bg-red-50 rounded-lg text-muted-foreground hover:text-destructive transition-all"
          aria-label={contact._deleted ? 'Undo delete' : 'Delete contact'}
        >
          {contact._deleted ? <Plus className="w-4 h-4" aria-hidden="true" /> : <Trash2 className="w-4 h-4" aria-hidden="true" />}
        </button>
      </TableCell>
    </TableRow>
  );
}

/* ── Contacts Tab (read-only + edit mode) ── */
function ContactsTab({
  contacts: initialContacts,
  studentId,
  canEdit,
  onSelectContact,
  onSaved,
}: {
  contacts: Contact[];
  studentId: string;
  canEdit: boolean;
  onSelectContact: (c: Contact) => void;
  onSaved: () => Promise<void> | void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editContacts, setEditContacts] = useState<EditableContact[]>([]);
  const [dialogContact, setDialogContact] = useState<EditableContact | null>(null);
  const [reorderedTypes, setReorderedTypes] = useState<Set<number>>(new Set());

  const enterEditMode = useCallback(() => {
    setEditContacts(initialContacts.map((c) => ({ ...c })));
    setEditing(true);
    setSaveError(null);
    setReorderedTypes(new Set());
  }, [initialContacts]);

  const cancelEdit = () => {
    setEditing(false);
    setEditContacts([]);
    setSaveError(null);
  };

  const addRow = (typeId: number) => {
    const maxRankForType = editContacts
      .filter((c) => !c._deleted && c.typeId === typeId)
      .reduce((max, c) => Math.max(max, c.rank), 0);

    const newContact: EditableContact = {
      id: `new-${Date.now()}`,
      studentId,
      typeId,
      rank: maxRankForType + 1,
      contactValue: { val: '' },
      isActive: true,
      isRecovery: false,
      bounced: false,
      bounceCount: 0,
      _isNew: true,
    };
    setEditContacts((prev) => [...prev, newContact]);
  };

  const addAddressOrEmergency = (typeId: number) => {
    const maxRankForType = editContacts
      .filter((c) => !c._deleted && c.typeId === typeId)
      .reduce((max, c) => Math.max(max, c.rank), 0);

    const newContact: EditableContact = {
      id: `new-${Date.now()}`,
      studentId,
      typeId,
      rank: maxRankForType + 1,
      contactValue: {},
      isActive: true,
      isRecovery: false,
      bounced: false,
      bounceCount: 0,
      _isNew: true,
    };
    // Add to list and immediately open dialog
    setEditContacts((prev) => {
      const newList = [...prev, newContact];
      setDialogContact(newContact);
      return newList;
    });
  };

  const openDialogForRow = (index: number) => {
    setDialogContact(editContacts[index]);
  };

  const handleDialogConfirm = (updated: EditableContact) => {
    if (dialogContact) {
      const targetId = dialogContact.id;
      setEditContacts((prev) => prev.map((c) => (c.id === targetId ? updated : c)));
    }
    setDialogContact(null);
  };

  const handleDialogCancel = () => {
    if (dialogContact?._isNew) {
      const id = dialogContact.id;
      setEditContacts((prev) => prev.filter((c) => c.id !== id));
    }
    setDialogContact(null);
  };

  const updateRow = (index: number, updated: EditableContact) => {
    setEditContacts((prev) => {
      const old = prev[index];
      // If the type changed, auto-compute the rank for the new type
      if (old && old.typeId !== updated.typeId) {
        const maxRankForNewType = prev
          .filter((c, i) => i !== index && !c._deleted && c.typeId === updated.typeId)
          .reduce((max, c) => Math.max(max, c.rank), 0);
        updated = { ...updated, rank: maxRankForNewType + 1 };
      }
      return prev.map((c, i) => (i === index ? updated : c));
    });
  };

  const toggleDeleteRow = (id: string) => {
    setEditContacts((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        return { ...c, _deleted: !c._deleted };
      })
    );
  };

  // Group contacts by typeId for drag-and-drop sections
  const groupedByType = useMemo(() => {
    const sorted = [...editContacts].sort((a, b) => a.typeId - b.typeId || a.rank - b.rank);
    const groups = new Map<number, EditableContact[]>();
    for (const c of sorted) {
      const list = groups.get(c.typeId);
      if (list) list.push(c);
      else groups.set(c.typeId, [c]);
    }
    return groups;
  }, [editContacts]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = (typeId: number) => (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setEditContacts((prev) => {
      // Extract the items for this type, preserving their original indices
      const typeItems = prev.filter((c) => c.typeId === typeId);
      const oldIndex = typeItems.findIndex((c) => c.id === active.id);
      const newIndex = typeItems.findIndex((c) => c.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;

      const reordered = arrayMove(typeItems, oldIndex, newIndex);
      // Reassign ranks based on new order
      const reranked = reordered.map((c, i) => ({ ...c, rank: i + 1 }));

      // Rebuild the full list: replace items of this type with reranked ones
      const otherItems = prev.filter((c) => c.typeId !== typeId);
      return [...otherItems, ...reranked];
    });
    setReorderedTypes((prev) => new Set(prev).add(typeId));
  };

  const handleSave = async () => {
    setSaveError(null);
    // Validate all non-deleted rows
    const active = editContacts.filter((c) => !c._deleted);
    const allErrors: string[] = [];
    for (const c of active) {
      const rowErrors = validateRow(c);
      if (rowErrors.length > 0) {
        const typeName = CONTACT_TYPE_NAMES[c.typeId] || `Type ${c.typeId}`;
        allErrors.push(`${typeName} (rank ${c.rank}): ${rowErrors.join('; ')}`);
      }
    }
    if (allErrors.length > 0) {
      setSaveError(allErrors.join('\n'));
      return;
    }

    setSaving(true);
    try {
      await saveContactsToApi(studentId, editContacts, reorderedTypes);
      await onSaved();
      setEditing(false);
      setEditContacts([]);
      setSaveError(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save contacts');
    } finally {
      setSaving(false);
    }
  };

  // ── Read-only view ──
  if (!editing) {
    const sorted = [...initialContacts].sort((a, b) => a.typeId - b.typeId || a.rank - b.rank);
    return (
      
      <div>
        {/* Modify button — bottom right */}
        {canEdit && (
          <div className="flex justify-end mt-4 pb-6">
            <Button variant="outline" className="gap-2" onClick={enterEditMode} aria-label="Modify contacts">
              <Pencil className="w-4 h-4" aria-hidden="true" /> Modify
            </Button>
          </div>
        )}
        {sorted.length === 0 ? (
          <div className="bg-white rounded-xl sm:rounded-2xl p-8 sm:p-12 shadow-sm text-center text-muted-foreground text-sm">
            No contacts found.
          </div>
        ) : (
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm overflow-hidden">
            {/* Mobile card layout */}
            <div className="lg:hidden divide-y divide-surface-low">
              {sorted.map((c) => {
                const typeName = CONTACT_TYPE_NAMES[c.typeId] || `Type ${c.typeId}`;
                const value = c.contactValue.val || c.contactValue.formattedAddress || '';
                const icon = TYPE_ICONS[c.typeId] || <Globe className="w-4 h-4" />;
                const colorClass = TYPE_COLORS[c.typeId] || 'bg-surface-low text-muted-foreground';
                return (
                  <button key={c.id} className="w-full text-left p-3 hover:bg-surface-low/50 transition-colors flex items-center gap-2.5" onClick={() => onSelectContact(c)}>
                    <div className={`w-8 h-8 rounded-lg ${colorClass} flex items-center justify-center shrink-0`}>{icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-semibold text-foreground">{typeName}</span>
                        {c.rank === 1 && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-[9px] font-black text-white">1</span>}
                        {c.isActive ? <Badge variant="success">Active</Badge> : c.bounced ? <Badge variant="destructive">Suspended</Badge> : <Badge variant="secondary">Inactive</Badge>}
                      </div>
                      <p className={`text-xs font-mono truncate ${c.bounced ? 'text-muted-foreground line-through' : 'text-foreground/70'}`}>{value}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Desktop table layout */}
            <div className="hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow className="bg-surface-low/50">
                  <TableHead className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Type</TableHead>
                  <TableHead className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Value</TableHead>
                  <TableHead className="text-center text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Rank</TableHead>
                  <TableHead className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Status</TableHead>
                  <TableHead className="text-center text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Bounced</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((c) => {
                  const typeName = CONTACT_TYPE_NAMES[c.typeId] || `Type ${c.typeId}`;
                  const value = c.contactValue.val || c.contactValue.formattedAddress || '';
                  const icon = TYPE_ICONS[c.typeId] || <Globe className="w-4 h-4" />;
                  const colorClass = TYPE_COLORS[c.typeId] || 'bg-surface-low text-muted-foreground';
                  return (
                    <TableRow key={c.id} className="hover:bg-surface-low/50 transition-colors cursor-pointer" onClick={() => onSelectContact(c)}>
                      <TableCell className="py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg ${colorClass} flex items-center justify-center`}>{icon}</div>
                          <span className="text-sm font-semibold text-foreground">{typeName}</span>
                        </div>
                      </TableCell>
                      <TableCell className={`font-mono text-xs ${c.bounced ? 'text-muted-foreground line-through' : 'text-foreground/70'}`}>{value}</TableCell>
                      <TableCell className="text-center">
                        {c.rank === 1
                          ? <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-[10px] font-black text-white">1</span>
                          : <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-surface-low text-[10px] font-black text-muted-foreground">{c.rank}</span>}
                      </TableCell>
                      <TableCell>
                        {c.isActive ? <Badge variant="success">Active</Badge> : c.bounced ? <Badge variant="destructive">Suspended</Badge> : <Badge variant="secondary">Inactive</Badge>}
                      </TableCell>
                      <TableCell className="text-center">
                        {c.bounced ? <AlertCircle className="w-5 h-5 text-destructive mx-auto" aria-label="Contact has bounced" /> : <CheckCircle2 className="w-5 h-5 text-surface-highest mx-auto" aria-label="No bounces" />}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
            <div className="px-6 py-3 bg-surface-low/30 border-t border-surface-low">
              <span className="text-xs text-muted-foreground font-medium">{sorted.length} contact{sorted.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Edit mode ──
  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-semibold text-muted-foreground">Editing contacts</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="gap-2" onClick={() => addAddressOrEmergency(6)} aria-label="Add address">
            <MapPin className="w-4 h-4" aria-hidden="true" /> Address
          </Button>
          <Button size="sm" variant="outline" className="gap-2" onClick={() => addAddressOrEmergency(12)} aria-label="Add emergency contact">
            <Star className="w-4 h-4" aria-hidden="true" /> Emergency
          </Button>
          <select
            onChange={(e) => { const v = Number(e.target.value); if (v) addRow(v); e.target.value = ''; }}
            defaultValue=""
            className="h-8 rounded-lg px-3 text-xs font-bold bg-surface-low text-foreground hover:bg-surface-container cursor-pointer appearance-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label="Add a new contact"
          >
            <option value="" disabled>+ Add contact…</option>
            {Object.entries(CONTACT_TYPE_NAMES)
              .filter(([id]) => !ADDRESS_TYPE_IDS.has(Number(id)) && Number(id) !== 12)
              .map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-surface-low/50">
              <TableHead className="w-16 text-[10px] sm:text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Rank</TableHead>
              <TableHead className="text-[10px] sm:text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Type</TableHead>
              <TableHead className="text-[10px] sm:text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Value</TableHead>
              <TableHead className="text-[10px] sm:text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Active</TableHead>
              <TableHead className="text-center text-[10px] sm:text-[11px] font-bold text-muted-foreground uppercase tracking-widest" />
            </TableRow>
          </TableHeader>
          {[...groupedByType.entries()].map(([typeId, group]) => (
            <DndContext key={typeId} sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd(typeId)}>
              <SortableContext items={group.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                <TableBody>
                  <TableRow className="bg-surface-low/30">
                    <TableCell colSpan={5} className="py-1.5 px-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded ${TYPE_COLORS[typeId] || 'bg-surface-low text-muted-foreground'} flex items-center justify-center`}>
                          {TYPE_ICONS[typeId]}
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                          {CONTACT_TYPE_NAMES[typeId] || `Type ${typeId}`}
                        </span>
                        <span className="text-[9px] font-bold text-muted-foreground/50">{group.length}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                  {group.map((c) => {
                    const idx = editContacts.findIndex((ec) => ec.id === c.id);
                    return (
                      <EditableRow
                        key={c.id}
                        contact={c}
                        onChange={(updated) => updateRow(idx, updated)}
                        onDelete={() => toggleDeleteRow(c.id)}
                        onOpenDialog={() => openDialogForRow(idx)}
                      />
                    );
                  })}
                </TableBody>
              </SortableContext>
            </DndContext>
          ))}
          {editContacts.length === 0 && (
            <TableBody>
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground text-sm">
                  No contacts. Click "Add" to create one.
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </div>

      {/* Validation errors */}
      {saveError && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 whitespace-pre-line" role="alert">
          {saveError}
        </div>
      )}

      {/* Save / Cancel */}
      <div className="flex justify-end gap-3 mt-4">
        <Button variant="outline" onClick={cancelEdit} disabled={saving}>Cancel</Button>
        <Button className="gap-2" onClick={handleSave} disabled={saving}>
          {saving ? (
            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true" /> Saving...</>
          ) : (
            <><Save className="w-4 h-4" aria-hidden="true" /> Save</>
          )}
        </Button>
      </div>

      {dialogContact && (
        <ContactEditDialog
          contact={dialogContact}
          onConfirm={handleDialogConfirm}
          onCancel={handleDialogCancel}
        />
      )}
    </div>
  );
}

/* ── Main ProfilePage Component ── */
export function ProfilePage() {
  const auth = useAppAuth();
  const isAdmin = useIsAdminOrStaff();
  const { studentId: paramStudentId, entityId: paramEntityId } = useParams<{
    studentId: string;
    entityId: string;
  }>();

  const [activeTab, setActiveTab] = useState<TabId>('contacts');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const claims = auth.user?.profile;
  const claimsStudentId = claims?.['custom:studentId'] as string | undefined;

  const { student, entity, contacts, loading, error, refetchContacts } = useProfileData(
    paramStudentId,
    paramEntityId,
    claimsStudentId,
  );

  const entityName = useEntityName(paramEntityId) || entity?.name;

  // Derive the student's primary email from their highest-ranked email contact (typeId 1)
  const primaryEmail = useMemo(() => {
    const emailContacts = contacts
      .filter((c) => c.typeId === 1 && c.contactValue.val)
      .sort((a, b) => a.rank - b.rank);
    return emailContacts[0]?.contactValue.val || '';
  }, [contacts]);

  const firstName = student?.firstName || (claims?.given_name as string) || '';
  const lastName = student?.lastName || (claims?.family_name as string) || '';
  const email = primaryEmail || (claims?.email as string) || '';
  const studentIdDisplay = student?.studentId || (claims?.['custom:studentId'] as string) || 'N/A';
  const entityId = student?.entityId || (claims?.['custom:entityId'] as string) || 'N/A';
  const name = `${firstName} ${lastName}`.trim() || email || 'Unknown';
  const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || '?';
  const resolvedStudentId = student?.id || (claims?.['custom:studentId'] as string) || '';

  // Admins/staff can edit contacts when viewing a student (entity route),
  // but cannot edit their own contacts on /profile. Students can always edit their own.
  const isViewingOwnProfile = !paramStudentId;
  const canEditContacts = isViewingOwnProfile ? !isAdmin : true;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" aria-hidden="true" />
        <span className="sr-only">Loading profile</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4" role="alert">
        <p className="text-destructive text-sm font-medium">{error}</p>
        <Button variant="outline" onClick={refetchContacts}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="flex items-center text-[11px] font-bold tracking-widest text-muted-foreground uppercase mb-6 animate-fade-up">
        {isAdmin && paramEntityId ? (
          <>
            <Link to="/entities" className="hover:text-primary transition-colors">Entities</Link>
            <span className="mx-2 w-[1px] h-3 bg-surface-highest inline-block" aria-hidden="true" />
            <Link to={`/entities/${paramEntityId}/students`} className="hover:text-primary transition-colors">{entityName || paramEntityId}</Link>
            <span className="mx-2 w-[1px] h-3 bg-surface-highest inline-block" aria-hidden="true" />
            <span className="text-primary" aria-current="page">{name}</span>
          </>
        ) : (
          <>
            <span>Registry</span>
            <span className="mx-2 w-[1px] h-3 bg-surface-highest inline-block" aria-hidden="true" />
            <span className="text-primary" aria-current="page">My Profile</span>
          </>
        )}
      </nav>

      {/* Hero Section */}
      <section aria-label="Student profile summary" className="bg-white rounded-xl sm:rounded-3xl p-4 sm:p-8 shadow-sm mb-6 sm:mb-8 relative overflow-hidden animate-fade-up-delay-1">
        <div className="absolute top-0 right-0 w-64 h-64 bg-accent/50 rounded-full -translate-y-1/2 translate-x-1/2" aria-hidden="true" />
        <div className="relative z-10 flex flex-col md:flex-row items-start gap-4 sm:gap-8">
          <div className="flex-shrink-0 relative">
            <div className="w-16 h-16 sm:w-32 sm:h-32 rounded-xl sm:rounded-3xl bg-gradient-to-br from-primary to-indigo-800 flex items-center justify-center text-xl sm:text-4xl font-bold text-white shadow-xl shadow-indigo-100" aria-hidden="true">
              {initials}
            </div>
            <div className="absolute -bottom-2 -right-2 bg-white p-1 rounded-xl shadow-md" aria-hidden="true">
              <Shield className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-primary" />
            </div>
          </div>
          <div className="flex-grow min-w-0">
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-2">
              <h2 className="text-lg sm:text-[32px] font-extrabold text-foreground tracking-tight leading-none">{name}</h2>
              <Badge variant="success">{student?.status || 'Active'}</Badge>
            </div>
            <p className="text-muted-foreground font-medium text-sm sm:text-lg mb-3 sm:mb-6 tracking-tight">
              Student ID: <span className="text-foreground font-bold">{studentIdDisplay}</span>
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:gap-8">
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">Email</span>
                <span className="text-sm font-semibold text-foreground/80 break-all">{email || 'N/A'}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">Entity</span>
                <span className="text-sm font-semibold text-foreground/80">{entityName || entityId}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">Enrollment</span>
                <span className="text-sm font-semibold text-foreground/80 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
                  {student?.enrollmentDate
                    ? new Date(student.enrollmentDate).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
                    : '01 Sept 2021'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabbed Carousel */}
      <section className="animate-fade-up-delay-2">
        <div className="grid grid-cols-2 sm:inline-flex sm:flex-row gap-1 mb-6 bg-white rounded-xl sm:rounded-2xl p-1.5 shadow-sm" role="tablist" aria-label="Profile sections">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => tab.enabled && setActiveTab(tab.id)}
                disabled={!tab.enabled}
                role="tab"
                aria-selected={active}
                aria-controls={`tabpanel-${tab.id}`}
                id={`tab-${tab.id}`}
                className={cn(
                  'flex items-center justify-center gap-1.5 px-3 py-2 sm:px-5 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all',
                  active
                    ? 'bg-primary text-white shadow-lg shadow-primary/20'
                    : tab.enabled
                      ? 'text-muted-foreground hover:bg-surface-low hover:text-foreground'
                      : 'text-muted-foreground/40 cursor-not-allowed'
                )}
              >
                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden="true" />
                {tab.label}
                {!tab.enabled && <span className="text-[8px] sm:text-[9px] font-bold uppercase tracking-widest opacity-60">Soon</span>}
              </button>
            );
          })}
        </div>

        <div role="tabpanel" id={`tabpanel-${activeTab}`} aria-labelledby={`tab-${activeTab}`}>
        {activeTab === 'contacts' && (
          <ContactsTab contacts={contacts} studentId={resolvedStudentId} canEdit={canEditContacts} onSelectContact={setSelectedContact} onSaved={refetchContacts} />
        )}
        {activeTab === 'records' && (
          <div className="bg-white rounded-xl sm:rounded-2xl p-8 sm:p-12 shadow-sm text-center text-muted-foreground text-sm">Records coming soon.</div>
        )}
        {activeTab === 'results' && (
          <div className="bg-white rounded-xl sm:rounded-2xl p-8 sm:p-12 shadow-sm text-center text-muted-foreground text-sm">Results coming soon.</div>
        )}
        {activeTab === 'finance' && (
          <div className="bg-white rounded-xl sm:rounded-2xl p-8 sm:p-12 shadow-sm text-center text-muted-foreground text-sm">Finance coming soon.</div>
        )}
        </div>
      </section>

      {selectedContact && <ContactSidebar contact={selectedContact} onClose={() => setSelectedContact(null)} />}
    </div>
  );
}
